import AsyncValidator from 'async-validator'
import { notEmpty } from '../utils/validate'
import { type } from "../utils/assist"

function notNull(v) {
    return v !== undefined && v !== null
}

// 可复合表单域
export default {
    provide() {
        return {
            field: this,
        }
    },
    
    inject: {
        parentField: {
            from: "field",
            default: null,
        },
        formItem: {
            from: "formItem",
            default: null,
        },
    },
    
    props: {
        // 数据模型
        model: {
            type: Object,
            default: null,
        },
        // 是否只读
        readonly: {
            type: Boolean,
            default: null,
        },
        // 是否禁用
        disabled: {
            type: Boolean,
            default: null,
        },
        // 常用校验——是否必填
        required: {
            type: Boolean,
            default: null,
        },
        // 校验规则
        rules: {
            type: [Array, Object],
        },
        // 属性名，可以时obj-path，用于在group中定位自身的位置
        prop: {
            type: String,
        },
        // 数据合并方式
        populateType: {
            type: String,
            default: "embed",
            validator(val) {
                return ["merge", "embed", "chain", "through"].indexOf(val) >= 0
            }
        },
        // 数据收集时，是否展开到父级
        spreadOut: {
            type: Boolean,
            default: false,
        },
        // 是否参与数数据收集
        unpopulated: {
            type: Boolean,
            default: false,
        },
    },
    
    data() {
        return {
            fieldChildren: [], // 子域
            isComposed: false, // 是否被组合
            hasRuleRequired: false,
            validateState: '',
            validateMessage: '',
        }
    },
    
    computed: {
        isEndField() {
            return this.fieldChildren.length <= 0
        },
        
        finalModel() {
            if (this.model === null) {
                return this.parentField && this.parentField.finalModel
            }
            
            return this.model
        },
        
        finalReadonly() {
            if (this.readonly === null) {
                return this.parentField && !!this.parentField.finalReadonly
            }
            
            return this.readonly
        },
        
        finalDisabled() {
            if (this.disabled === null) {
                return this.parentField && !!this.parentField.finalDisabled
            }
            
            return this.disabled
        },
        
        finalRequired() {
            if (this.required === null) {
                return this.parentField && this.parentField.finalRequired
            }
            
            return this.required
        },
        
        finalProp() {
            if (this.prop) {
                return this.prop
            }
            
            // @向后兼容，继承formItem指定的prop
            if (this.formItem) {
                return this.formItem.prop
            }
            
            return this.$options.name || 'value'
        },
        
        // 最终规则
        finalRules() {
            // 优先取自身
            if (this.rules) {
                return this.rules
            }
            
            // 末级与非末级规则不同，前者是按prop映射，后者是透明传输
            if (this.parentField && this.parentField.finalRules) {
                if (this.isEndField || this.composed) {
                    return this.parentField.finalRules[this.finalProp] || []
                }
                
                return this.parentField.finalRules
            }
            
            return []
        },
        
        // 是否需要展开数据
        fSpreadOut() {
            return this.spreadOut
        },
    },
    
    watch: {
        rules() {
            this.setRules()
        }
    },
    
    beforeCreate() {
        // 是否组合节点。组合节点跟其下的子节点会被视为一个整体
        this.composed = !!this.$options.composed
    },
    
    created() {
        if (this.parentField) {
            this.parentField.addField(this)
            
            // 判断是否被组合
            if (this.parentField.composed || this.parentField.isComposed) {
                this.isComposed = true
            }
        }
    },
    
    beforeDestroy() {
        if (this.parentField) {
            this.parentField.removeField(this)
        }
    },
    
    methods: {
        // 添加子域
        addField(vm) {
            this.fieldChildren.push(vm)
        },
        
        // 移除子域
        removeField(vm) {
            var i = this.fieldChildren.indexOf(vm)
            
            if (i >= 0) {
                this.fieldChildren.splice(i, 1)
            }
        },
        
        // 重置状态
        resetField() {
            this.validateDone()
            this.fieldChildren.forEach(v => v.resetField())
        },
        
        // 校验接口
        validate(trigger, callback) {
            if (arguments.length < 2) {
                callback = trigger
                trigger = ''
            }
            
            return new Promise((resolve, reject) => {
                this.recValidate(trigger, errors => {
                    errors.length <= 0 ? resolve() : reject(errors)
                    
                    if (typeof callback == "function") {
                        // 函数签名(isValid, errors)
                        callback(errors.length <= 0, errors)
                    }
                })
            })
        },
        
        // 递归校验
        recValidate(trigger, callback) {
            if (this.isEndField || this.composed) {
                this.checkValue(trigger, errors => this.validateDone(errors, callback))
            } else {
                let count = 0,
                    total = this.fieldChildren.length,
                    allErrors = []
                
                this.fieldChildren.forEach(field => {
                    field.recValidate(trigger, errors => {
                        if (errors) {
                            allErrors.push(...errors)
                        }
                        
                        // all finish
                        if (++count === total) {
                            this.validateDone(allErrors, callback)
                        }
                    })
                })
            }
        },
        
        // 重置验证状态
        resetValidation(){
            this.validateState = 'success'
            this.validateMessage = ''
            
            this.fieldChildren.forEach(v => v.resetValidation())
        },

        // 校验结束
        validateDone(errors, cb) {
            if (errors === null) {
                errors = []
            }
            
            this.validateState = errors.length ? 'error' : 'success'
            this.validateMessage = errors.length ? errors[0].message : ''
            
            // user callback
            if (typeof cb == "function") {
                // (valid, errorStack)
                cb(errors)
            }
        },
        
        // 父级校验某一个子域
        validateField(prop, callback) {
            const field = this.fieldChildren.find(field => field.prop === prop)
            if (!field) {
                throw new Error('[iView warn]: must call validateField with valid prop string!')
            }
            
            field.validate('', cb)
        },
        
        // @interface 值校验
        checkValue(trigger, cb) {
            let rules = this.getFilteredRule(trigger)
            
            // 保证required时，必然有非空校验
            if (this.finalRequired && !this.hasRuleRequired) {
                rules.unshift({ validator: notEmpty })
            }
            
            // 无需校验
            if (rules.length === 0) {
                return cb(null)
            }
            
            // 开始校验
            this.validateState = 'validating'
            
            let descriptor = {}
            descriptor[this.finalProp] = rules
            
            const validator = new AsyncValidator(descriptor)
            let model = Object.assign({}, this.finalModel)
            
            // model中，当前field可能不是最新的(在v-model改变之前触发)
            model[this.finalProp] = this.getCheckValue()
            
            validator.validate(model, { firstFields: true }, errors => cb(errors))
        },
        
        // 过滤校验规则
        getFilteredRule(trigger) {
            var rules = []
            
            if (type(this.finalRules) == "array") {
                this.finalRules.forEach(v => {
                    if (!v.trigger || v.trigger.indexOf(trigger) !== -1) {
                        // required校验转换为notEmpty
                        if (v.required) {
                            rules.push({
                                validator: notEmpty,
                                message: v.message || '',
                            })
                        } else {
                            rules.push(v)
                        }
                    }
                })
            }
            
            return rules
        },
        
        // 规则变动，更新相关状态
        setRules() {
            if (this.isEndField) {
                // 是否包含必填rule
                this.hasRuleRequired = this.finalRules.findIndex(v => v.required) >= 0
                
                // 错误状态下的组件，重新校验
                if (this.validateState == "error") {
                    this.validate()
                }
            } else {
                this.fieldChildren.forEach(v => v.setRules())
            }
        },
        
        // @interface 获取当前value
        getCurrentValue() {
            if (typeof this.value != "undefined") {
                return this.value
            }
            
            throw new Error(`${this.$options.name}未提供value属性或者未实现getCurrentValue方法`)
        },
        
        // @interface 获取校验value
        getCheckValue() {
            // 实际场景中：
            // 1. 表单收集时，一般使用字符值方便，对应getCurrentValue
            // 2. 表单检验时，一般使用原始值方便，对应getCheckValue（默认跟getCurrentValue相同）
            return this.getCurrentValue()
        },
        
        // 收集数据
        populateData() {
            if (this.isEndField || this.composed) {
                // value
                return this.getCurrentValue()
            } else {
                var populateType = this.populateType,
                    data = populateType === "chain" ? [] : {}
                
                this.fieldChildren
                    .filter(v => v.unpopulated === false)
                    .forEach(v => {
                        var subData = v.populateData(),
                            spread = v.fSpreadOut
                        
                        if (populateType == "through") {
                            data = subData
                        }
                        
                        if (populateType == "merge") {
                            spread ?
                                Object.assign(data, { ...subData }) :
                                Object.assign(data, subData)
                        }
                        
                        if (populateType == "embed" && notNull(subData)) {
                            spread ?
                                Object.assign(data, subData) :
                                data[v.finalProp] = subData
                        }
                        
                        if (populateType == "chain" && notNull(subData)) {
                            spread ?
                                data.push(...subData.filter(w => notNull(w))) :
                                data.push(subData)
                        }
                    })
                
                return data
            }
        },
        
        // 通知FormItem
        dispatchFormChange(val) {
            // 被组合的组件，不再单独发射事件
            if (this.isComposed) {
                return
            }
            
            if (this.formItem) {
                this.formItem.$emit('on-form-change', val)
            }
        },
        
        dispatchFormBlur(val) {
            // 被组合的组件，不再单独发射事件
            if (this.isComposed) {
                return
            }
            
            if (this.formItem) {
                this.formItem.$emit('on-form-blur', val)
            }
        },
    }
}